// cspell:words crossfade yourname Hauser
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CameraAlt as CameraIcon,
  Email as EmailIcon,
  Home as LicenseIcon,
  Language as LanguageIcon,
  LocationOnOutlined as LocationOutlinedIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import HauserLogo from '@/assets/images/hauser-logo.svg?react';
import { MaskedTextField } from '@/components/common/MaskedTextField';
import {
  ALL_MARKETS,
  LANGUAGES,
  MORE_LANGUAGES,
  POPULAR_MARKETS,
  REFERRAL_SOURCES,
  YEARS_OF_EXPERIENCE,
} from '@/constants/profileOptions';
import { useAuthContext } from '@/context/AuthProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUserProfile } from '@/hooks/user/useUserProfile';
import { getAvatarUploadUrl } from '@/services/api/users/api';
import { useTranslationPrefix } from '@/translations/i18n';
import { toProperCase } from '@/utils/properCase';

const font = "'Manrope', sans-serif";

const getCroppedImg = (imageSrc: string, pixelCrop: Area): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas context failed'));
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
      );
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    image.onerror = reject;
  });

const textFieldSx = {
  '& .MuiInputBase-root': { backgroundColor: '#333333', borderRadius: '10px', height: 40 },
  '& .MuiOutlinedInput-notchedOutline': { borderRadius: '10px' },
  '& .MuiInputBase-input': {
    fontSize: 13,
    fontFamily: font,
    letterSpacing: '0.02em',
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px #333333 inset',
      WebkitTextFillColor: 'var(--tx-text)',
      caretColor: 'var(--tx-text)',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: 13, fontFamily: font, letterSpacing: '0.02em' },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E47192',
  },
  '& .MuiFormHelperText-root': {
    fontFamily: font,
    fontSize: 11,
    color: '#E47192',
    mt: '4px',
    ml: 0,
  },
};

const expiryInputSx = {
  ...textFieldSx,
  '& .MuiInputBase-root': { backgroundColor: '#333333', borderRadius: '10px', height: 40 },
  '& .MuiInputBase-input': { ...textFieldSx['& .MuiInputBase-input'], padding: '0 4px 0 14px' },
};

const EXPIRY_MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const CURRENT_YEAR = new Date().getFullYear();
const EXPIRY_YEARS = Array.from({ length: 20 }, (_, i) => CURRENT_YEAR + i);

const dropdownPaperSx = {
  // a menu surface is the solid popover (Tradehaus .tx-pop), never the 5% tile tint that lets the page show through
  backgroundColor: 'var(--tx-dialog)',
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow-sm)',
  borderRadius: '12px',
  mt: '4px',
  '& .MuiAutocomplete-option': {
    fontFamily: font,
    fontSize: 13,
    color: 'var(--tx-text)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
    '&[aria-selected="true"]': { backgroundColor: 'rgba(187,183,175,0.15)' },
  },
};

// ── Validation ───────────────────────────────────────────────────────────────

const validationSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  phoneNumber: yup
    .number()
    .required('Phone number is required')
    .test('exactly-10-digits', 'Enter a valid phone number', (value) => {
      const length = value?.toString().length ?? 0;
      return length === 10;
    })
    .typeError('Enter a valid phone number'),
  dreNumber: yup.string().required('DRE license is required'),
  calLink: yup.string().required('Cal.com URL is required'),
  yearsExperience: yup.string().required('Years of experience is required'),
  markets: yup.array().of(yup.string()).min(1, 'Select at least one market'),
  languages: yup.array().of(yup.string()).min(1, 'Select at least one language'),
});

// ── Form values ──────────────────────────────────────────────────────────────

interface OnboardingValues {
  firstName: string;
  lastName: string;
  phoneNumber: number | '';
  dreNumber: string;
  dreExpiry: string;
  linkedinUrl: string;
  calLink: string;
  markets: string[];
  yearsExperience: string;
  referralSource: string;
  languages: string[];
  notes: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export const AdvisorOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const { userProfile, isInitialFetchingUserProfile, updateUserProfile, isUpdatingUserProfile } =
    useUserProfile();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cropper state
  const [showCelebration, setShowCelebration] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCropConfirm = async () => {
    if (cropImage && croppedAreaPixels) {
      const cropped = await getCroppedImg(cropImage, croppedAreaPixels);
      setAvatarPreview(cropped);
    }
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const formik = useFormik<OnboardingValues>({
    initialValues: {
      firstName: userProfile?.firstName || user?.firstName || '',
      lastName: userProfile?.lastName || user?.lastName || '',
      phoneNumber: userProfile?.phoneNumber || '',
      dreNumber: '',
      dreExpiry: '',
      linkedinUrl: '',
      calLink: userProfile?.calLink || '',
      markets: [],
      yearsExperience: '',
      referralSource: '',
      languages: [],
      notes: '',
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        let photoUrl: string | undefined;

        if (avatarPreview) {
          const blob = await fetch(avatarPreview).then((r) => r.blob());
          const mimeType = (blob.type || 'image/jpeg') as 'image/jpeg' | 'image/png' | 'image/webp';
          const { presignedUrl, publicUrl } = await getAvatarUploadUrl(mimeType);
          const uploadRes = await fetch(presignedUrl, {
            method: 'PUT',
            body: blob,
            headers: { 'Content-Type': mimeType },
          });
          if (!uploadRes.ok) throw new Error('Avatar upload failed');
          // Store the stable URL — cache-busting is applied at render time via updatedAt
          photoUrl = publicUrl;
        }

        await updateUserProfile(
          {
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNumber: values.phoneNumber || undefined,
            dreNumber: values.dreNumber || undefined,
            dreExpiry: values.dreExpiry || undefined,
            markets: values.markets.length ? values.markets : undefined,
            languages: values.languages.length ? values.languages : undefined,
            linkedinUrl: values.linkedinUrl || undefined,
            calLink: values.calLink || undefined,
            yearsExperience: values.yearsExperience || undefined,
            referralSource: values.referralSource || undefined,
            ...(photoUrl ? { photoUrl } : {}),
          },
          { markUserProfileAsComplete: true },
        );
        setShowCelebration(true);
        setTimeout(() => navigate('/'), 3000);
      } catch {
        showAlert(t('errors.generic'), 'error');
      }
    },
  });

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImage(reader.result as string);
        setAvatarError(false);
      };
      reader.readAsDataURL(file);
    }
    if (e.target) e.target.value = '';
  };

  const toggleMarket = (market: string) => {
    const current = formik.values.markets;
    formik.setFieldValue(
      'markets',
      current.includes(market) ? current.filter((m) => m !== market) : [...current, market],
    );
  };

  const toggleLanguage = (lang: string) => {
    const current = formik.values.languages;
    formik.setFieldValue(
      'languages',
      current.includes(lang) ? current.filter((l) => l !== lang) : [...current, lang],
    );
  };

  const handleNextPage = () => {
    // Validate page 1 fields before advancing
    formik.validateForm().then((errors) => {
      if (
        !errors.firstName &&
        !errors.lastName &&
        !errors.phoneNumber &&
        !errors.dreNumber &&
        !errors.calLink
      ) {
        setPage(1);
      } else {
        formik.setTouched({
          firstName: true,
          lastName: true,
          phoneNumber: true,
          dreNumber: true,
          calLink: true,
        });
      }
    });
  };

  const handleGetStarted = () => {
    formik.validateForm().then((errors) => {
      const missingAvatar = !avatarPreview;
      const hasErrors = Object.keys(errors).length > 0 || missingAvatar;
      setAvatarError(missingAvatar);
      if (hasErrors) {
        formik.setTouched({
          firstName: true,
          lastName: true,
          phoneNumber: true,
          dreNumber: true,
          calLink: true,
          yearsExperience: true,
          markets: true,
          languages: true,
        } as Record<string, boolean>);

        const needsPage1 =
          errors.firstName ||
          errors.lastName ||
          errors.phoneNumber ||
          errors.dreNumber ||
          errors.calLink ||
          missingAvatar;

        showAlert(
          needsPage1
            ? 'Please complete the highlighted fields on the first step'
            : 'Please complete the highlighted fields',
          'error',
        );

        if (needsPage1) {
          setPage(0);
        }

        // Scroll to first error after touched state propagates
        setTimeout(() => {
          const root = document.querySelector('[data-testid="advisor-onboarding"]');
          const firstError = root?.querySelector(
            '.Mui-error, [data-error="true"]',
          ) as HTMLElement | null;
          firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      } else {
        formik.handleSubmit();
      }
    });
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (isInitialFetchingUserProfile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
        }}>
        <CircularProgress sx={{ color: 'var(--tx-muted)' }} />
      </Box>
    );
  }

  // ── Initials for avatar fallback ───────────────────────────────────────────

  const initials = [formik.values.firstName, formik.values.lastName]
    .map((n) => n?.[0] || '')
    .join('')
    .toUpperCase();

  // ── Profile completeness ───────────────────────────────────────────────────

  const filledFields = [
    avatarPreview,
    formik.values.firstName,
    formik.values.lastName,
    formik.values.phoneNumber,
    formik.values.dreNumber,
    formik.values.yearsExperience,
    formik.values.markets.length > 0,
    formik.values.languages.length > 0,
    formik.values.calLink,
  ].filter(Boolean).length;
  const totalFields = 9;
  const completeness = Math.round((filledFields / totalFields) * 100);

  // Format phone for preview
  const phoneStr = formik.values.phoneNumber ? String(formik.values.phoneNumber) : '';
  const formattedPhone =
    phoneStr.length === 10
      ? `(${phoneStr.slice(0, 3)}) ${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`
      : '';

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Box
        data-testid="advisor-onboarding"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          p: { xs: 2, sm: 4 },
        }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 1200,
            borderRadius: '24px',
            backgroundColor: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            overflow: 'hidden',
          }}>
          {/* Full-width 2-column grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr',
              minHeight: 560,
            }}>
            {/* ── LEFT: Header + Form ───────────────────────────────────── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: '#262626' }}>
              {/* Header */}
              <Box
                sx={{ px: isMobile ? '20px' : '25px', pt: isMobile ? '20px' : '25px', pb: '10px' }}>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontWeight: 600,
                    fontSize: 22,
                    color: 'var(--tx-text)',
                    lineHeight: 1.3,
                  }}>
                  Onboarding
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 13,
                    color: 'var(--tx-faint)',
                    mt: 0.5,
                  }}>
                  Complete your profile to get started.
                </Typography>
              </Box>

              {/* Page indicator */}
              <Box
                sx={{ px: isMobile ? '20px' : '25px', pt: '4px', pb: 1, display: 'flex', gap: 1 }}>
                {[0, 1].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      height: 3,
                      flex: 1,
                      borderRadius: 2,
                      backgroundColor: i <= page ? 'var(--tx-fill-2)' : 'var(--tx-fill-2)',
                      transition: 'background-color 0.3s ease',
                    }}
                  />
                ))}
              </Box>

              {/* Form pages — fixed size, crossfade */}
              <Box
                sx={{ position: 'relative', overflow: 'hidden', height: isMobile ? 'auto' : 520 }}>
                {/* Page 0 */}
                <Box
                  sx={{
                    position: isMobile ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    width: isMobile ? '100%' : undefined,
                    opacity: page === 0 ? 1 : 0,
                    pointerEvents: page === 0 ? 'auto' : 'none',
                    transition: 'opacity 0.25s ease',
                    display: isMobile && page !== 0 ? 'none' : 'block',
                  }}>
                  <Page1Identity
                    formik={formik}
                    avatarPreview={avatarPreview}
                    avatarError={avatarError}
                    initials={initials}
                    email={user?.email || ''}
                    handleAvatarClick={handleAvatarClick}
                    handleAvatarChange={handleAvatarChange}
                    fileInputRef={fileInputRef}
                  />
                </Box>
                {/* Page 1 */}
                <Box
                  sx={{
                    position: isMobile ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    width: isMobile ? '100%' : undefined,
                    opacity: page === 1 ? 1 : 0,
                    pointerEvents: page === 1 ? 'auto' : 'none',
                    transition: 'opacity 0.25s ease',
                    display: isMobile && page !== 1 ? 'none' : 'block',
                  }}>
                  <Page2Professional
                    formik={formik}
                    toggleMarket={toggleMarket}
                    toggleLanguage={toggleLanguage}
                  />
                </Box>
              </Box>

              {/* Footer / Navigation */}
              <Box
                sx={{
                  px: isMobile ? '20px' : '25px',
                  pt: 3,
                  pb: isMobile ? '20px' : '25px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                {/* Left side: Back (page 2 only) */}
                <Box>
                  {page === 1 && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setPage(0)}
                      sx={{
                        fontFamily: font,
                        fontWeight: 500,
                        fontSize: 14,
                        letterSpacing: '-0.02em',
                        color: 'var(--tx-muted)',
                        textTransform: 'none',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        '&:hover': { color: 'var(--tx-text)' },
                      }}>
                      <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.5 }} />
                      Back
                    </Box>
                  )}
                </Box>

                {/* Right side: Next / Submit */}
                {page === 0 ? (
                  <Button
                    type="button"
                    onClick={handleNextPage}
                    sx={{
                      fontFamily: font,
                      fontWeight: 600,
                      fontSize: 14,
                      letterSpacing: '-0.02em',
                      backgroundColor: 'var(--tx-fill-2)',
                      color: 'var(--tx-text)',
                      borderRadius: '10px',
                      minWidth: 120,
                      height: 40,
                      position: 'relative',
                      px: 4,
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#ccc8c0' },
                    }}>
                    Next
                    <ChevronRightIcon sx={{ fontSize: 18, position: 'absolute', right: '10px' }} />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleGetStarted}
                    disabled={isUpdatingUserProfile}
                    sx={{
                      fontFamily: font,
                      fontWeight: 600,
                      fontSize: 14,
                      letterSpacing: '-0.02em',
                      backgroundColor: completeness === 100 ? 'var(--tx-lime)' : 'var(--tx-fill-2)',
                      color: 'var(--tx-on-lime)',
                      borderRadius: '10px',
                      minWidth: 120,
                      height: 40,
                      px: 4,
                      textTransform: 'none',
                      transition: 'background-color 0.2s ease',
                      '&:hover': { backgroundColor: completeness === 100 ? '#d4ff4d' : '#ccc8c0' },
                      '&.Mui-disabled': { backgroundColor: '#bbb7af80', color: '#262626' },
                    }}>
                    {isUpdatingUserProfile ? (
                      <CircularProgress size={20} sx={{ color: '#262626' }} />
                    ) : (
                      'Get started'
                    )}
                  </Button>
                )}
              </Box>
            </Box>

            {/* ── RIGHT: Live Profile Preview ──────────────────────────── */}
            <Box
              sx={{
                borderLeft: { xs: 'none', md: '1px solid var(--tx-line)' },
                borderTop: { xs: '1px solid var(--tx-line)', md: 'none' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: isMobile ? '20px' : 5,
                backgroundColor: 'var(--tx-fill-2)',
                borderTopRightRadius: isMobile ? 0 : '20px',
                borderBottomRightRadius: '20px',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.06,
                  backgroundImage: `
                    linear-gradient(rgba(38,38,38,0.3) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(38,38,38,0.3) 1px, transparent 1px),
                    linear-gradient(rgba(38,38,38,0.15) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(38,38,38,0.15) 1px, transparent 1px)
                  `,
                  backgroundSize: '60px 60px, 60px 60px, 12px 12px, 12px 12px',
                  pointerEvents: 'none',
                },
              }}>
              <ProfilePreview
                avatarPreview={avatarPreview}
                initials={initials}
                firstName={formik.values.firstName}
                lastName={formik.values.lastName}
                email={user?.email || ''}
                phone={formattedPhone}
                dreNumber={formik.values.dreNumber}
                dreExpiry={formik.values.dreExpiry}
                markets={formik.values.markets}
                languages={formik.values.languages}
                linkedinUrl={formik.values.linkedinUrl}
                completeness={completeness}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Avatar crop overlay */}
      {cropImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--tx-backdrop)',
          }}
          onClick={() => setCropImage(null)}>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: '100%',
              maxWidth: 500,
              backgroundColor: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              borderRadius: '16px',
              boxShadow: 'var(--tx-shadow)',
              p: 3,
            }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 350,
                backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)',
                borderRadius: '12px',
                overflow: 'hidden',
              }}>
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
            <Stack direction="row" alignItems="center" sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 12,
                  color: 'var(--tx-faint)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  mr: '20px',
                }}>
                Adjust size
              </Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.05}
                onChange={(_, v) => setZoom(v as number)}
                sx={{
                  color: 'var(--tx-muted)',
                  '& .MuiSlider-thumb': { width: 25, height: 15, borderRadius: '6px' },
                }}
              />
            </Stack>
            <Stack direction="row" justifyContent="flex-end" gap={1.5} sx={{ mt: 2 }}>
              <Button
                onClick={() => setCropImage(null)}
                sx={{
                  fontFamily: font,
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: '-0.02em',
                  color: 'var(--tx-faint)',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'transparent', color: 'var(--tx-muted)' },
                }}>
                Cancel
              </Button>
              <Button
                onClick={handleCropConfirm}
                sx={{
                  fontFamily: font,
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: '-0.02em',
                  backgroundColor: 'var(--tx-fill-2)',
                  color: 'var(--tx-text)',
                  borderRadius: '10px',
                  px: 3,
                  py: 0.75,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#ccc8c0' },
                }}>
                Confirm
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
              backdropFilter: 'blur(20px)',
            }}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}>
              <ProfilePreview
                avatarPreview={avatarPreview}
                initials={initials}
                firstName={formik.values.firstName}
                lastName={formik.values.lastName}
                email={user?.email || ''}
                phone={formattedPhone}
                dreNumber={formik.values.dreNumber}
                dreExpiry={formik.values.dreExpiry}
                markets={formik.values.markets}
                languages={formik.values.languages}
                linkedinUrl={formik.values.linkedinUrl}
                completeness={100}
                hideLabel
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ── Page 1: Identity ─────────────────────────────────────────────────────────

const Page1Identity = ({
  formik,
  avatarPreview,
  avatarError,
  initials,
  email,
  handleAvatarClick,
  handleAvatarChange,
  fileInputRef,
}: {
  formik: ReturnType<typeof useFormik<OnboardingValues>>;
  avatarPreview: string | null;
  avatarError: boolean;
  initials: string;
  email: string;
  handleAvatarClick: () => void;
  handleAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}) => {
  const [dreExpiryMonth, setDreExpiryMonth] = useState(
    formik.values.dreExpiry ? formik.values.dreExpiry.slice(5, 7) : '',
  );
  const [dreExpiryDay, setDreExpiryDay] = useState(
    formik.values.dreExpiry ? formik.values.dreExpiry.slice(8, 10) : '',
  );
  const [dreExpiryYear, setDreExpiryYear] = useState(
    formik.values.dreExpiry ? formik.values.dreExpiry.slice(0, 4) : '',
  );

  const syncExpiry = (m: string, d: string, y: string) =>
    formik.setFieldValue('dreExpiry', m && d && y ? `${y}-${m}-${d}` : '');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Stack
      gap={isMobile ? '15px' : '25px'}
      sx={{
        px: isMobile ? '20px' : '25px',
        pt: isMobile ? '20px' : '22px',
        pb: isMobile ? '10px' : '25px',
      }}>
      <Typography
        sx={{
          fontFamily: font,
          fontWeight: 600,
          fontSize: 11,
          color: 'var(--tx-faint)',
        }}>
        Identity
      </Typography>

      {/* Avatar */}
      <Box
        data-error={avatarError || undefined}
        sx={{ position: 'relative', width: 64, mb: isMobile ? '5px' : 0 }}>
        <Avatar
          src={avatarPreview || undefined}
          sx={{
            width: 64,
            height: 64,
            backgroundColor: 'var(--tx-fill-2)',
            color: 'var(--tx-text)',
            fontFamily: font,
            fontWeight: 600,
            fontSize: 22,
            cursor: 'pointer',
            border: avatarError ? '2px solid #E47192' : 'none',
            transition: 'border 0.2s ease',
          }}
          onClick={handleAvatarClick}>
          {!avatarPreview && initials}
        </Avatar>
        <IconButton
          size="small"
          onClick={handleAvatarClick}
          sx={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            backgroundColor: 'var(--tx-fill)',
            border: '2px solid var(--tx-line)',
            color: 'var(--tx-muted)',
            width: 24,
            height: 24,
            '&:hover': { backgroundColor: '#444' },
          }}>
          <CameraIcon sx={{ fontSize: 12 }} />
        </IconButton>
        {!avatarPreview && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: -2,
              right: -2,
              color: '#E47192',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1,
            }}>
            *
          </Box>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleAvatarChange}
        />
      </Box>

      {/* Name row */}
      <Stack direction="row" gap={2}>
        <FieldGroup label="First name" required flex={1}>
          <TextField
            fullWidth
            name="firstName"
            value={formik.values.firstName}
            onChange={(e) => formik.setFieldValue('firstName', toProperCase(e.target.value))}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            sx={textFieldSx}
          />
        </FieldGroup>
        <FieldGroup label="Last name" required flex={1}>
          <TextField
            fullWidth
            name="lastName"
            value={formik.values.lastName}
            onChange={(e) => formik.setFieldValue('lastName', toProperCase(e.target.value))}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            sx={textFieldSx}
          />
        </FieldGroup>
      </Stack>

      {/* Email + Phone */}
      <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? '15px' : 2}>
        <FieldGroup label="Email" flex={1}>
          <TextField fullWidth disabled value={email} sx={textFieldSx} />
        </FieldGroup>
        <FieldGroup label="Phone" required flex={1}>
          <MaskedTextField
            fullWidth
            name="phoneNumber"
            maskProps={{
              mask: '(A00) 000-0000',
              definitions: { A: /[2-9]/ },
              lazy: false,
              placeholderChar: '#',
            }}
            value={String(formik.values.phoneNumber)}
            onChange={(value) =>
              formik.setFieldValue('phoneNumber', value.length > 0 ? Number(value) : '')
            }
            onBlur={formik.handleBlur}
            error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
            helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
            sx={textFieldSx}
          />
        </FieldGroup>
      </Stack>

      {/* Cal.com + LinkedIn */}
      <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? '15px' : 2}>
        <Stack gap={2} sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--tx-muted)',
              }}>
              Calendar
              <Box component="span" sx={{ color: '#E47192', ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <Box
              component="a"
              href="https://cal.com/signup"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: font,
                fontSize: 11,
                color: 'var(--tx-faint)',
                textDecoration: 'none',
                '&:hover': { color: 'var(--tx-muted)' },
              }}>
              Cal.com Signup →
            </Box>
          </Box>
          <TextField
            fullWidth
            name="calLink"
            placeholder="username/60min"
            value={formik.values.calLink}
            onChange={(e) => {
              const v = e.target.value
                .replace(/^https?:\/\/(www\.)?cal\.com\//i, '')
                .replace(/^(www\.)?cal\.com\//i, '');
              formik.setFieldValue('calLink', v);
            }}
            onBlur={(e) => {
              const v = e.target.value.replace(/\/+$/, '');
              formik.setFieldValue('calLink', v);
              formik.handleBlur(e);
            }}
            autoComplete="off"
            slotProps={{
              input: {
                startAdornment: (
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary2', fontSize: 14, whiteSpace: 'nowrap', mr: 0.5 }}>
                    cal.com/
                  </Box>
                ),
              },
            }}
            error={formik.touched.calLink && Boolean(formik.errors.calLink)}
            helperText={formik.touched.calLink && formik.errors.calLink}
            sx={textFieldSx}
          />
        </Stack>
        <FieldGroup label="LinkedIn profile" flex={1}>
          <TextField
            fullWidth
            name="linkedinUrl"
            placeholder="yourname"
            value={formik.values.linkedinUrl}
            onChange={(e) => {
              const v = e.target.value
                .replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, '')
                .replace(/\/+$/, '');
              formik.setFieldValue('linkedinUrl', v);
            }}
            autoComplete="off"
            slotProps={{
              input: {
                startAdornment: (
                  <Box
                    component="span"
                    sx={{ color: 'text.secondary2', fontSize: 14, whiteSpace: 'nowrap', mr: 0.5 }}>
                    linkedin.com/in/
                  </Box>
                ),
              },
            }}
            sx={textFieldSx}
          />
        </FieldGroup>
      </Stack>

      {/* DRE + Expiry */}
      <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? '15px' : 2}>
        <FieldGroup label="DRE license #" required flex={1}>
          <TextField
            fullWidth
            name="dreNumber"
            autoComplete="off"
            value={formik.values.dreNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.dreNumber && Boolean(formik.errors.dreNumber)}
            helperText={formik.touched.dreNumber && formik.errors.dreNumber}
            sx={textFieldSx}
          />
        </FieldGroup>
        <FieldGroup label="DRE expiry" flex={1}>
          {(() => {
            const daysInMonth =
              dreExpiryMonth && dreExpiryYear
                ? new Date(Number(dreExpiryYear), Number(dreExpiryMonth), 0).getDate()
                : 31;
            const dayOptions = Array.from({ length: daysInMonth }, (_, i) =>
              String(i + 1).padStart(2, '0'),
            );
            return (
              <Stack direction="row" gap={1} sx={{ width: '100%' }}>
                <Autocomplete
                  sx={{ flex: 0.7, minWidth: 0 }}
                  disableClearable
                  options={EXPIRY_MONTHS}
                  value={dreExpiryMonth || ''}
                  onChange={(_, val) => {
                    const m = val ?? '';
                    setDreExpiryMonth(m);
                    syncExpiry(m, dreExpiryDay, dreExpiryYear);
                  }}
                  ListboxProps={{ style: { maxHeight: 180 } }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [{ name: 'flip', enabled: false }],
                    },
                    paper: { sx: dropdownPaperSx },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="MM" sx={expiryInputSx} />
                  )}
                />
                <Autocomplete
                  sx={{ flex: 0.7, minWidth: 0 }}
                  disableClearable
                  options={dayOptions}
                  value={dreExpiryDay || ''}
                  onChange={(_, val) => {
                    const d = val ?? '';
                    setDreExpiryDay(d);
                    syncExpiry(dreExpiryMonth, d, dreExpiryYear);
                  }}
                  ListboxProps={{ style: { maxHeight: 180 } }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [{ name: 'flip', enabled: false }],
                    },
                    paper: { sx: dropdownPaperSx },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="DD" sx={expiryInputSx} />
                  )}
                />
                <Autocomplete
                  sx={{ flex: 1.2, minWidth: 0 }}
                  disableClearable
                  options={EXPIRY_YEARS.map(String)}
                  value={dreExpiryYear || ''}
                  onChange={(_, val) => {
                    const y = val ?? '';
                    setDreExpiryYear(y);
                    syncExpiry(dreExpiryMonth, dreExpiryDay, y);
                  }}
                  ListboxProps={{ style: { maxHeight: 180 } }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [{ name: 'flip', enabled: false }],
                    },
                    paper: { sx: dropdownPaperSx },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="YYYY" sx={expiryInputSx} />
                  )}
                />
              </Stack>
            );
          })()}
        </FieldGroup>
      </Stack>
    </Stack>
  );
};

// ── Page 2: Professional ─────────────────────────────────────────────────────

const Page2Professional = ({
  formik,
  toggleMarket,
  toggleLanguage,
}: {
  formik: ReturnType<typeof useFormik<OnboardingValues>>;
  toggleMarket: (market: string) => void;
  toggleLanguage: (lang: string) => void;
}) => {
  const [showMarketDropdown, setShowMarketDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const chipSx = (selected: boolean) => ({
    fontFamily: font,
    fontSize: 11,
    fontWeight: 600,
    borderRadius: '6px',
    height: 'auto',
    py: '4px',
    px: '10px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    backgroundColor: selected ? 'var(--tx-fill-3)' : 'var(--tx-fill)',
    color: selected ? 'var(--tx-text)' : 'var(--tx-muted)',
    border: 0,
    '& .MuiChip-label': { px: 0 },
    '&:hover': { backgroundColor: selected ? 'rgba(243,243,243,0.35)' : 'rgba(255,255,255,0.1)' },
  });

  const availableMarkets = ALL_MARKETS.filter((m) => !formik.values.markets.includes(m));

  return (
    <Stack
      gap={isMobile ? '20px' : '30px'}
      sx={{
        px: isMobile ? '20px' : '25px',
        pt: isMobile ? '20px' : '22px',
        pb: isMobile ? '10px' : '25px',
      }}>
      <Typography
        sx={{
          fontFamily: font,
          fontWeight: 600,
          fontSize: 11,
          color: 'var(--tx-faint)',
        }}>
        Professional
      </Typography>

      {/* Markets */}
      <FieldGroup label="Markets" required>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '5px' }}>
          {POPULAR_MARKETS.map((market) => {
            const selected = formik.values.markets.includes(market);
            return (
              <Chip
                key={market}
                label={market}
                onClick={() => toggleMarket(market)}
                sx={chipSx(selected)}
              />
            );
          })}
          {/* Custom market chips (non-popular selections) */}
          {formik.values.markets
            .filter((m) => !POPULAR_MARKETS.includes(m))
            .map((m) => (
              <Chip
                key={m}
                label={m}
                onDelete={() => toggleMarket(m)}
                sx={{
                  ...chipSx(true),
                  '& .MuiChip-deleteIcon': { color: 'var(--tx-muted)', fontSize: 14, ml: '6px' },
                  '& .MuiChip-deleteIcon:hover': { color: 'var(--tx-text)' },
                }}
              />
            ))}
          {/* "+ More options" chip */}
          {!showMarketDropdown && (
            <Chip
              label="+ More options"
              onClick={() => setShowMarketDropdown(true)}
              sx={{
                fontFamily: font,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: '6px',
                height: 'auto',
                py: '4px',
                px: '10px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                backgroundColor: 'var(--tx-fill)',
                color: 'var(--tx-muted)',
                border: '1px dashed var(--tx-line)',
                '& .MuiChip-label': { px: 0 },
                '&:hover': {
                  backgroundColor: 'var(--tx-fill-2)',
                  borderColor: 'var(--tx-line)',
                },
              }}
            />
          )}
        </Box>
        {showMarketDropdown && (
          <Autocomplete
            open
            options={availableMarkets}
            onChange={(_, val) => {
              if (val && !formik.values.markets.includes(val)) {
                formik.setFieldValue('markets', [...formik.values.markets, val]);
              }
              setShowMarketDropdown(false);
            }}
            onClose={() => setShowMarketDropdown(false)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search CA counties..."
                autoFocus
                error={formik.touched.markets && Boolean(formik.errors.markets)}
                helperText={formik.touched.markets && formik.errors.markets}
                sx={{
                  ...textFieldSx,
                  mt: 1,
                  '& .MuiInputBase-input::placeholder': { fontSize: 13, fontFamily: font },
                }}
              />
            )}
            slotProps={{ paper: { sx: dropdownPaperSx } }}
          />
        )}
      </FieldGroup>

      {/* Languages */}
      <FieldGroup label="Languages spoken" required>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '5px' }}>
          {LANGUAGES.map((lang) => {
            const selected = formik.values.languages.includes(lang);
            return (
              <Chip
                key={lang}
                label={lang}
                onClick={() => toggleLanguage(lang)}
                sx={chipSx(selected)}
              />
            );
          })}
          {/* Custom language chips (non-popular selections) */}
          {formik.values.languages
            .filter((l) => !LANGUAGES.includes(l))
            .map((l) => (
              <Chip
                key={l}
                label={l}
                onDelete={() => toggleLanguage(l)}
                sx={{
                  ...chipSx(true),
                  '& .MuiChip-deleteIcon': { color: 'var(--tx-muted)', fontSize: 14, ml: '6px' },
                  '& .MuiChip-deleteIcon:hover': { color: 'var(--tx-text)' },
                }}
              />
            ))}
          {/* "+ More options" chip */}
          {!showLangDropdown && (
            <Chip
              label="+ More options"
              onClick={() => setShowLangDropdown(true)}
              sx={{
                fontFamily: font,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: '6px',
                height: 'auto',
                py: '4px',
                px: '10px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                backgroundColor: 'var(--tx-fill)',
                color: 'var(--tx-muted)',
                border: '1px dashed var(--tx-line)',
                '& .MuiChip-label': { px: 0 },
                '&:hover': {
                  backgroundColor: 'var(--tx-fill-2)',
                  borderColor: 'var(--tx-line)',
                },
              }}
            />
          )}
        </Box>
        {showLangDropdown && (
          <Autocomplete
            open
            options={MORE_LANGUAGES.filter((l) => !formik.values.languages.includes(l))}
            onChange={(_, val) => {
              if (val && !formik.values.languages.includes(val)) {
                formik.setFieldValue('languages', [...formik.values.languages, val]);
              }
              setShowLangDropdown(false);
            }}
            onClose={() => setShowLangDropdown(false)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search languages..."
                autoFocus
                error={formik.touched.languages && Boolean(formik.errors.languages)}
                helperText={formik.touched.languages && formik.errors.languages}
                sx={{
                  ...textFieldSx,
                  mt: 1,
                  '& .MuiInputBase-input::placeholder': { fontSize: 13, fontFamily: font },
                }}
              />
            )}
            slotProps={{ paper: { sx: dropdownPaperSx } }}
          />
        )}
      </FieldGroup>

      {/* Years of Experience + Referral Source */}
      <Stack direction="row" gap={2}>
        <FieldGroup label="Experience" required flex={1}>
          <Autocomplete
            fullWidth
            options={YEARS_OF_EXPERIENCE}
            value={formik.values.yearsExperience || null}
            onChange={(_e, val) => formik.setFieldValue('yearsExperience', val || '')}
            slotProps={{
              paper: { sx: { fontSize: 12, fontFamily: font, color: 'var(--tx-fill-2)' } },
              listbox: {
                sx: {
                  '& .MuiAutocomplete-option': { fontSize: 12, fontFamily: font, color: 'var(--tx-muted)' },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                error={formik.touched.yearsExperience && Boolean(formik.errors.yearsExperience)}
                helperText={formik.touched.yearsExperience && formik.errors.yearsExperience}
                sx={{
                  ...textFieldSx,
                  '& .MuiInputBase-input::placeholder': { fontSize: 13, fontFamily: font },
                }}
              />
            )}
          />
        </FieldGroup>
        <FieldGroup label="Referral source" flex={1}>
          <Autocomplete
            fullWidth
            options={REFERRAL_SOURCES}
            value={formik.values.referralSource || null}
            onChange={(_e, val) => formik.setFieldValue('referralSource', val || '')}
            slotProps={{
              paper: { sx: { fontSize: 12, fontFamily: font, color: 'var(--tx-fill-2)' } },
              listbox: {
                sx: {
                  '& .MuiAutocomplete-option': { fontSize: 12, fontFamily: font, color: 'var(--tx-muted)' },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                sx={{
                  ...textFieldSx,
                  '& .MuiInputBase-input::placeholder': { fontSize: 13, fontFamily: font },
                }}
              />
            )}
          />
        </FieldGroup>
      </Stack>

      {/* Bio */}
      <FieldGroup label="Bio">
        <TextField
          fullWidth
          multiline
          rows={3}
          name="notes"
          placeholder="Tell clients a bit about yourself..."
          value={formik.values.notes}
          onChange={formik.handleChange}
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-root': { ...textFieldSx['& .MuiInputBase-root'], height: 'auto' },
            '& .MuiInputBase-input::placeholder': { fontSize: 13, fontFamily: font },
          }}
        />
      </FieldGroup>
    </Stack>
  );
};

// ── Live Profile Preview ─────────────────────────────────────────────────────

const ProfilePreview = ({
  avatarPreview,
  initials,
  firstName,
  lastName,
  email,
  phone,
  dreNumber,
  dreExpiry: _dreExpiry,
  markets,
  languages,
  linkedinUrl,
  completeness,
  hideLabel,
}: {
  avatarPreview: string | null;
  initials: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dreNumber: string;
  dreExpiry: string;
  markets: string[];
  languages: string[];
  linkedinUrl: string;
  completeness: number;
  hideLabel?: boolean;
}) => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <Box
      sx={{
        width: 300,
        position: 'relative',
      }}>
      {/* Preview label */}
      {!hideLabel && (
        <Typography
          sx={{
            fontFamily: font,
            fontWeight: 600,
            fontSize: 10,
            color: 'var(--tx-text)',
            mb: 2,
            textAlign: 'center',
          }}>
          Live preview
        </Typography>
      )}

      {/* Card — portrait orientation */}
      <Box
        sx={{
          borderRadius: '24px',
          backgroundColor: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          backdropFilter: 'blur(16px)',
          overflow: 'hidden',
          position: 'relative',
          minHeight: 500,
          display: 'flex',
          flexDirection: 'column',
        }}>
        {/* Centered avatar + name hero area */}
        <Box
          sx={{
            pt: '80px',
            pb: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            px: '25px',
          }}>
          {/* Avatar with completeness ring */}
          <Box sx={{ position: 'relative', mb: '25px' }}>
            {!hideLabel && (
              <svg
                width="88"
                height="88"
                viewBox="0 0 88 88"
                style={{ position: 'absolute', top: -5, left: -5 }}>
                <circle
                  cx="44"
                  cy="44"
                  r="41"
                  fill="none"
                  stroke="rgba(255,255,255,0.04)"
                  strokeWidth="2"
                />
                <circle
                  cx="44"
                  cy="44"
                  r="41"
                  fill="none"
                  stroke={completeness === 100 ? '#c0ff02' : '#bbb7af'}
                  strokeWidth="2"
                  strokeDasharray={`${2 * Math.PI * 41}`}
                  strokeDashoffset={`${2 * Math.PI * 41 * (1 - completeness / 100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 44 44)"
                  style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.4s ease' }}
                />
              </svg>
            )}
            <Avatar
              src={avatarPreview || undefined}
              sx={{
                width: 78,
                height: 78,
                backgroundColor: 'var(--tx-fill-2)',
                color: 'var(--tx-text)',
                fontFamily: font,
                fontWeight: 600,
                fontSize: 24,
              }}>
              {!avatarPreview && (initials || '?')}
            </Avatar>
          </Box>

          {/* Name */}
          <AnimatePresence mode="wait">
            <motion.div
              key={fullName || 'placeholder'}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontWeight: 600,
                  fontSize: 17,
                  color: 'var(--tx-text)',
                  lineHeight: 1.3,
                  textAlign: 'left',
                }}>
                {fullName || 'Your Name'}
              </Typography>
            </motion.div>
          </AnimatePresence>

          <Typography
            sx={{
              fontFamily: font,
              fontSize: 12,
              color: 'var(--tx-faint)',
              mt: 0.5,
            }}>
            Real Estate Advisor
          </Typography>
        </Box>

        {/* Divider */}
        <Box
          sx={{ mx: '25px', mt: '20px', mb: '20px', borderTop: '1px solid rgba(255,255,255,0.05)' }}
        />

        {/* Contact & details section */}
        <Stack gap={1.5} sx={{ px: '25px' }}>
          <PreviewRow icon={<EmailIcon />} value={email} placeholder="email@example.com" />
          <PreviewRow icon={<PhoneIcon />} value={phone} placeholder="(###) ###-####" />
          <PreviewRow
            icon={<LicenseIcon />}
            value={dreNumber ? `DRE #${dreNumber}` : ''}
            placeholder="DRE license"
          />
        </Stack>

        {/* Spacer pushes logo + completeness to bottom */}
        <Box sx={{ flex: 1 }} />

        {/* Logo + icons row — above completeness */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            px: '25px',
            pb: '20px',
          }}>
          <HauserLogo style={{ width: 125, height: 'auto', color: '#bbb7af' }} />
          {(markets.length > 0 || languages.length > 0 || linkedinUrl) && (
            <Stack spacing="15px" sx={{ alignItems: 'center' }}>
              {markets.length > 0 && (
                <Tooltip
                  title={
                    <Stack spacing={0.5}>
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--tx-muted)',
                          pb: '2px',
                        }}>
                        Markets
                      </Typography>
                      {markets.map((m) => (
                        <Typography
                          key={m}
                          sx={{ fontFamily: font, fontSize: 11, fontWeight: 600 }}>
                          {m}
                        </Typography>
                      ))}
                    </Stack>
                  }
                  placement="left"
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)',
                        borderRadius: '12px',
                        px: '12px',
                        py: '8px',
                        color: 'var(--tx-text)',
                      },
                    },
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <LocationOutlinedIcon sx={{ fontSize: 28, color: '#333333' }} />
                  </Box>
                </Tooltip>
              )}
              {languages.length > 0 && (
                <Tooltip
                  title={
                    <Stack spacing={0.5}>
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--tx-muted)',
                          pb: '2px',
                        }}>
                        Languages
                      </Typography>
                      {languages.map((l) => (
                        <Typography
                          key={l}
                          sx={{ fontFamily: font, fontSize: 11, fontWeight: 600 }}>
                          {l}
                        </Typography>
                      ))}
                    </Stack>
                  }
                  placement="left"
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)',
                        borderRadius: '12px',
                        px: '12px',
                        py: '8px',
                        color: 'var(--tx-text)',
                      },
                    },
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <LanguageIcon sx={{ fontSize: 28, color: '#333333' }} />
                  </Box>
                </Tooltip>
              )}
              {linkedinUrl && (
                <Tooltip
                  title="View profile"
                  placement="left"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontFamily: font,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)',
                        borderRadius: '12px',
                        px: '12px',
                        py: '8px',
                        color: 'var(--tx-text)',
                      },
                    },
                  }}>
                  <Box
                    component="a"
                    href={`https://www.linkedin.com/in/${linkedinUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}>
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="#333333"
                      xmlns="http://www.w3.org/2000/svg">
                      {/* eslint-disable-next-line max-len */}
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                  </Box>
                </Tooltip>
              )}
            </Stack>
          )}
        </Box>

        {/* Completeness footer */}
        {!hideLabel && (
          <Box
            sx={{
              px: '25px',
              py: 2,
              borderTop: '1px solid var(--tx-line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 11,
                color: 'var(--tx-muted)',
              }}>
              Profile completeness
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 48,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: 'var(--tx-fill)',
                  overflow: 'hidden',
                }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${completeness}%`,
                    borderRadius: 2,
                    backgroundColor: completeness === 100 ? 'var(--tx-lime)' : 'var(--tx-fill-2)',
                    transition: 'width 0.4s ease, background-color 0.4s ease',
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 11,
                  fontWeight: 600,
                  color: completeness === 100 ? 'var(--tx-lime-ink)' : 'var(--tx-faint)',
                  transition: 'color 0.4s ease',
                  minWidth: 32,
                  textAlign: 'right',
                }}>
                {completeness}%
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Preview row ──────────────────────────────────────────────────────────────

const PreviewRow = ({
  icon,
  value,
  placeholder,
}: {
  icon: React.ReactNode;
  value: string;
  placeholder: string;
}) => (
  <Stack direction="row" alignItems="center" gap={1.5}>
    <Box
      sx={{
        color: value ? 'var(--tx-faint)' : '#8b868040',
        display: 'flex',
        alignItems: 'center',
        transition: 'color 0.3s ease',
        '& .MuiSvgIcon-root': { fontSize: 15 },
      }}>
      {icon}
    </Box>
    <Typography
      sx={{
        fontFamily: font,
        fontSize: 12,
        color: value ? 'var(--tx-muted)' : '#8b868040',
        transition: 'color 0.3s ease',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
      {value || placeholder}
    </Typography>
  </Stack>
);

// ── Shared field wrapper ─────────────────────────────────────────────────────

const FieldGroup = ({
  label,
  required,
  flex,
  children,
}: {
  label: string;
  required?: boolean;
  flex?: number;
  children: React.ReactNode;
}) => (
  <Stack gap={2} sx={{ flex, minWidth: 0 }}>
    <Typography
      sx={{
        fontFamily: font,
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--tx-muted)',
      }}>
      {label}
      {required && (
        <Box component="span" sx={{ color: '#E47192', ml: 0.5 }}>
          *
        </Box>
      )}
    </Typography>
    {children}
  </Stack>
);
